import React, { useState } from 'react';
import type { Debt, DebtStatus } from '../types/debt';
import { deleteDebt } from '../services/debtService';
import { Pencil, Trash2, PlusCircle, ChevronDown, ChevronUp, CheckCircle2, Clock, AlertCircle, Building } from 'lucide-react';

interface DebtCardProps {
  debt: Debt;
  onEdit: (debt: Debt) => void;
  onPayment: (debt: Debt) => void;
  onDeleted: () => void;
}

const STATUS_CONFIG: Record<DebtStatus, { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: 'Active', color: 'text-amber-400 bg-amber-400/10', icon: Clock },
  partial: { label: 'Paying', color: 'text-indigo-400 bg-indigo-400/10', icon: AlertCircle },
  paid: { label: 'Paid Off', color: 'text-emerald-400 bg-emerald-400/10', icon: CheckCircle2 },
};

const DebtCard: React.FC<DebtCardProps> = ({ debt, onEdit, onPayment, onDeleted }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const remaining = debt.amount - debt.amountPaid;
  const progress = debt.amount > 0 ? (debt.amountPaid / debt.amount) * 100 : 0;
  const isOverdue = debt.dueDate && debt.status !== 'paid' && debt.dueDate < new Date().toISOString().split('T')[0];

  const { label, color, icon: StatusIcon } = STATUS_CONFIG[debt.status];

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    await deleteDebt(debt.id);
    onDeleted();
  };

  return (
    <div className="glass-card rounded-2xl overflow-hidden transition-all duration-200 hover:border-white/15 fade-in-up">
      {/* Main row */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center">
              <Building className="h-5 w-5 text-indigo-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{debt.creditorName}</p>
              <p className="text-xs text-gray-500 capitalize mt-0.5">{debt.category.replace(/_/g, ' ')}</p>
            </div>
          </div>

          <div className="flex-shrink-0 text-right">
            <p className="text-base font-bold text-white">
              {debt.currency} {remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide mt-1.5 ${color}`}>
              <StatusIcon className="h-3 w-3" />
              {label}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {debt.status !== 'pending' && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
              <span>Paid {debt.currency} {debt.amountPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500 bg-indigo-500" style={{ width: `${Math.min(progress, 100)}%` }} />
            </div>
          </div>
        )}

        {/* Meta row */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {isOverdue && <span className="text-red-400 font-medium">⚠ Overdue</span>}
            {debt.dueDate && !isOverdue && debt.status !== 'paid' && <span>Due {new Date(debt.dueDate).toLocaleDateString()}</span>}
            {debt.status === 'paid' && <span className="text-emerald-400">Fully settled</span>}
          </div>
          <div className="flex items-center gap-1">
            {debt.status !== 'paid' && (
              <button onClick={() => onPayment(debt)} className="p-1.5 rounded-lg text-emerald-400 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors" title="Record payment">
                <PlusCircle className="h-4 w-4" />
              </button>
            )}
            <button onClick={() => onEdit(debt)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors" title="Edit">
              <Pencil className="h-4 w-4" />
            </button>
            <button onClick={handleDelete}
              className={`p-1.5 rounded-lg transition-colors ${confirmDelete ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'}`}
              title={confirmDelete ? 'Click again to confirm delete' : 'Delete'}>
              <Trash2 className="h-4 w-4" />
            </button>
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded view */}
      {expanded && (
        <div className="px-5 pb-5 border-t pt-4 space-y-3" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          {debt.description && (
            <p className="text-sm text-gray-400">{debt.description}</p>
          )}
          {debt.payments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Payment History</p>
              <div className="space-y-2">
                {[...debt.payments].reverse().map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 text-gray-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                      <span>{new Date(p.date).toLocaleDateString()}</span>
                      {p.note && <span className="text-gray-600 truncate max-w-[120px]">· {p.note}</span>}
                    </div>
                    <span className="text-emerald-400 font-medium">+ {debt.currency} {p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs text-gray-600 pt-2 border-t mt-3" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            Added {new Date(debt.createdAt).toLocaleDateString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtCard;
